import DateFormatToken from '@/constants/date/DateFormatToken'
import { DurationUnits } from '@/constants/date/DurationUnits'
import dayjs from '@/services/date/index'

const EXPECTED_FORMAT = DateFormatToken.HHmm

/**
 * Represents a time range with a start and end time in 24-hour format.
 */
export default class TimeRange {
  /**
   * @param {string} start - The start time in "HH:mm" 24-hour format.
   * @param {string} end - The end time in "HH:mm" 24-hour format.
   * @throws {Error} If either start or end time is not a valid "HH:mm" format.
   */
  constructor(start, end) {
    if (!TimeRange.isValidTimeFormat(start)) {
      throw new Error(
        `Invalid start time format: "${start}". Expected "${EXPECTED_FORMAT}".`
      )
    }

    if (!TimeRange.isValidTimeFormat(end)) {
      throw new Error(
        `Invalid end time format: "${end}". Expected "${EXPECTED_FORMAT}".`
      )
    }

    /**
     * @type {string}
     * @example '09:30'
     */
    this.start = start

    /**
     * @type {string}
     * @example '15:30'
     */
    this.end = end
  }

  /**
   * Validates a time string against the "HH:mm" 24-hour format.
   * @param {string} timeStr
   * @returns {boolean}
   */
  static isValidTimeFormat(timeStr) {
    const time = dayjs(timeStr, EXPECTED_FORMAT, true) // strict parsing
    return time.isValid()
  }

  /**
   * Converts the start time to a Day.js object.
   * @returns {dayjs.Dayjs}
   */
  getStartTime() {
    return dayjs(this.start, DateFormatToken.HHmm)
  }

  /**
   * Converts the end time to a Day.js object.
   * @returns {dayjs.Dayjs}
   */
  getEndTime() {
    return dayjs(this.end, DateFormatToken.HHmm)
  }

  /**
   * Calculates the duration of the time range in minutes.
   * @returns {number}
   */
  getDurationInMinutes() {
    return this.getEndTime().diff(this.getStartTime(), DurationUnits.MINUTE)
  }

  /**
   * Checks if another TimeRange is identical to this one.
   * @param {TimeRange} other
   * @returns {boolean}
   */
  isEqualTo(other) {
    return this.start === other.start && this.end === other.end
  }
}
